import { Button, Col, Input, Row, Table } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ColumnProps } from 'antd/es/table';
import { useEffect, useState } from 'react';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { CURRENCY_FORMAT } from '@common/constants';

import { addDataRecords, deleteDataRecord } from './helpers/detail-entry-helpers';
import { DetailDataRecord } from './models/DetailDataRecord';

import './SelectedProductDetailsTable.scss';

interface SelectedProductDetailsTableProps {
    tableData: DetailDataRecord[];
    setTableData: (newData: DetailDataRecord[]) => void;
    unitPrice: number;
    onConfirm: () => void;
    onCancel: () => void;
}

export const SelectedProductDetailsTable = ({
    tableData,
    setTableData,
    unitPrice,
    onConfirm,
    onCancel,
}: SelectedProductDetailsTableProps): JSX.Element => {
    const [isDataValid, setIsDataValid] = useState(true);
    const [totalPrice, setTotalPrice] = useState(0);

    useEffect(() => {
        setTotalPrice(
            tableData.reduce((previousValue, currentValue): number => {
                return previousValue + currentValue.quantity * unitPrice;
            }, 0)
        );
        setIsDataValid(
            !tableData.some(
                (item) => (item.quantity > 0 && !item.note.trim()) || (item.quantity === 0 && item.note.trim())
            )
        );
    }, [tableData, unitPrice]);

    const onInputChange = (key: string, index: number) => {
        return (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
            const newData = [...tableData];
            if (key === 'quantity') {
                const qty = Number(e.target.value);
                newData[index].quantity = Math.max(0, Number.isNaN(qty) ? 0 : Math.min(qty, 10000));
            } else if (key === 'note') {
                newData[index].note = String(e.target.value);
            }
            setTableData(newData);
        };
    };

    const onDeleteClick = (key: string) => {
        setTableData(deleteDataRecord(tableData, key));
    };

    const columns: ColumnProps<DetailDataRecord>[] = [
        {
            dataIndex: 'quantity',
            title: 'Quantity',
            width: 15,
            className: 'quantityColumn',
            render: (text, record, index) => (
                <Input
                    className={record.quantity === 0 && record.note.trim() ? 'ProductDetailsTable--alert' : ''}
                    value={text}
                    onChange={onInputChange('quantity', index)}
                />
            ),
        },
        {
            dataIndex: 'note',
            title: 'Note',
            width: 55,
            className: 'noteColumn',
            render: (text, record, index) => (
                <TextArea
                    rows={2}
                    className={record.quantity > 0 && !record.note.trim() ? 'ProductDetailsTable--alert' : ''}
                    value={text}
                    placeholder={record.quantity > 0 ? 'Required' : undefined}
                    onChange={onInputChange('note', index)}
                />
            ),
        },
        {
            title: 'Price',
            width: 15,
            align: 'right',
            className: 'priceColumn',
            render: (_text, record) => <span>{CURRENCY_FORMAT.format(record.quantity * unitPrice)}</span>,
        },
        {
            width: 15,
            align: 'center',
            className: 'actionColumn',
            render: (_text, record) => (
                <Button
                    size="small"
                    tabIndex={-1}
                    className="ProductDetailsTable__delete-row"
                    onClick={() => onDeleteClick(record.key)}
                >
                    <DeleteOutlined />
                </Button>
            ),
        },
    ];

    const onAddButtonClick = () => {
        setTableData(addDataRecords(tableData));
    };

    return (
        <div className="ProductDetailsTable">
            <div className="ProductDetailsTable__total-price">Total: {CURRENCY_FORMAT.format(totalPrice)}</div>
            <div className="ProductDetailsTable__table">
                <Table
                    tableLayout="fixed"
                    scroll={{ y: 500 }}
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                />
            </div>
            <Row justify="space-between" align="middle" className="ProductDetailsTable__footer">
                <Col className="ProductDetailsTable__add-row">
                    <Button icon={<PlusOutlined />} type="link" onClick={onAddButtonClick}>
                        Add a row
                    </Button>
                </Col>
                <Col className="ProductDetailsTable__close-buttons">
                    <Row align="middle">
                        {!isDataValid ? <span>Enter Required Data</span> : null}
                        <Button
                            disabled={!isDataValid}
                            type="primary"
                            onClick={onConfirm}
                            className="ProductDetailsTable__confirm"
                        >
                            OK
                        </Button>
                        <Button onClick={onCancel} type="primary" className="ProductDetailsTable__cancel">
                            Cancel
                        </Button>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};
